import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatusBadge, fmtDate, fmtDateTime } from "@/components/common";
import DataTable from "@/components/DataTable";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { CalendarPlus, Clock, Check, X } from "lucide-react";
import { toast } from "sonner";
import { LeaveTypes } from "@/components/LeaveTypes";

const hm = (m) => `${Math.floor((m || 0) / 60)}h ${(m || 0) % 60}m`;
const t = (s) => (s ? new Date(s).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : "—");
const MGR = ["super_admin", "admin", "admission_manager", "management", "team_lead"];
const ADMIN = ["super_admin", "admin"];
const err = (e) => toast.error(formatApiError(e.response?.data?.detail));
const today = () => new Date().toISOString().slice(0, 10);

function AttendanceLog({ isMgr }) {
  const [from, setFrom] = useState(new Date(Date.now() - 6 * 864e5).toISOString().slice(0, 10));
  const [to, setTo] = useState(today());
  const [rows, setRows] = useState(null);
  useEffect(() => { setRows(null); api.get(`/attendance?date_from=${from}&date_to=${to}`).then(({ data }) => setRows(data)); }, [from, to]);
  const columns = [
    ...(isMgr ? [{ key: "user_name", label: "Name", fixed: true, render: (r) => <span className="font-semibold">{r.user_name}</span> }] : []),
    { key: "date", label: "Date" },
    { key: "in", label: "First In", render: (r) => t(r.sessions?.[0]?.in) },
    { key: "out", label: "Last Out", render: (r) => { const l = r.sessions?.[r.sessions.length - 1]; return l?.out ? t(l.out) : <span className="text-emerald-600 font-semibold">Working</span>; } },
    { key: "sessions", label: "Sessions", render: (r) => <span className="text-xs text-slate-500" title={(r.sessions || []).map((s) => `${fmtDateTime(s.in)} → ${s.out ? fmtDateTime(s.out) : "…"}`).join("\n")}>{r.sessions?.length || 0}</span> },
    { key: "minutes", label: "Worked", render: (r) => <span className="font-semibold">{hm(r.minutes)}</span> },
    { key: "regularized", label: "Note", render: (r) => (r.regularized ? <span className="text-[11px] px-2 py-0.5 rounded-full bg-sky-50 text-sky-700 font-semibold">Regularised</span> : "") },
  ];
  return (
    <>
      <div className="flex gap-2 mb-3">
        <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-40 bg-white" data-testid="att-from" />
        <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-40 bg-white" data-testid="att-to" />
      </div>
      <DataTable columns={columns} rows={rows} loading={rows === null} testid="attendance-table" rowTestid="att-row" columnsKey="attendance" empty="No attendance records" />
    </>
  );
}

function DecideButtons({ r, onDecide, isMgr, me, isAdmin }) {
  const open = ["Pending", "Manager Approved"].includes(r.status);
  const canApprove = r.status === "Pending" || isAdmin;
  if (!open && !(r.kind === "leave" && r.status === "Approved" && r.user_id === me && r.hr_id)) return <span className="text-[11px] text-slate-400">{r.decided_by ? `by ${r.decided_by}` : ""}</span>;
  return (
    <div className="flex gap-1">
      {open && isMgr && r.user_id !== me && <>
        {canApprove && <Button size="sm" variant="outline" className="h-7 px-2 text-emerald-700" onClick={() => onDecide(r, "Approved")} data-testid={`approve-${r.id}`}><Check className="w-3.5 h-3.5" /></Button>}
        <Button size="sm" variant="outline" className="h-7 px-2 text-rose-600" onClick={() => onDecide(r, "Rejected")} data-testid={`reject-${r.id}`}><X className="w-3.5 h-3.5" /></Button>
      </>}
      {r.user_id === me && r.kind === "leave" && <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => onDecide(r, "Cancelled")} data-testid={`cancel-${r.id}`}>Cancel</Button>}
    </div>
  );
}

function LeaveDialog({ open, onOpenChange, onSaved, types }) {
  const [f, setF] = useState({});
  useEffect(() => { if (open) setF({ type_id: types[0]?.type_id, type: types[0]?.type, from_date: today(), to_date: today(), half_day: false, reason: "" }); }, [open]); // eslint-disable-line
  const save = async () => { try { const { data } = await api.post("/leaves", f); toast.success(data?.lop_days ? `Leave submitted · ${data.lop_days} day(s) will be Loss of Pay` : "Leave request submitted"); onOpenChange(false); onSaved(); } catch (e) { err(e); } };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white"><DialogHeader><DialogTitle>Apply for Leave</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label>Leave type</Label>
            <Select value={String(f.type_id ?? f.type ?? "")} onValueChange={(v) => { const x = types.find((y) => String(y.type_id ?? y.type) === v); setF({ ...f, type_id: x?.type_id, type: x?.type }); }}><SelectTrigger data-testid="leave-type"><SelectValue placeholder="Pick a leave type" /></SelectTrigger>
              <SelectContent className="bg-white">{types.map((x) => <SelectItem key={x.type_id ?? x.type} value={String(x.type_id ?? x.type)}>{x.type}{x.quota ? ` (${x.balance} left)` : ""}</SelectItem>)}</SelectContent></Select>
            <p className="text-[11px] text-slate-400 mt-1">Sundays are not counted. Days beyond your balance become Loss of Pay. Synced with admin.ohcampus.com HR.</p></div>
          <div><Label>From</Label><Input type="date" value={f.from_date || ""} onChange={(e) => setF({ ...f, from_date: e.target.value, to_date: f.to_date < e.target.value ? e.target.value : f.to_date })} data-testid="leave-from" /></div>
          <div><Label>To</Label><Input type="date" value={f.to_date || ""} min={f.from_date} onChange={(e) => setF({ ...f, to_date: e.target.value })} data-testid="leave-to" /></div>
          {f.from_date === f.to_date && <label className="col-span-2 flex items-center gap-2 text-sm"><input type="checkbox" className="accent-[#4F46E5]" checked={!!f.half_day} onChange={(e) => setF({ ...f, half_day: e.target.checked })} data-testid="leave-half" /> Half day</label>}
          <div className="col-span-2"><Label>Reason</Label><Textarea value={f.reason || ""} onChange={(e) => setF({ ...f, reason: e.target.value })} data-testid="leave-reason" /></div>
        </div>
        <DialogFooter><Button onClick={save} className="bg-[#4F46E5] hover:bg-[#4338CA]" data-testid="leave-submit">Submit</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Leaves({ isMgr, me, isAdmin }) {
  const [rows, setRows] = useState(null);
  const [bal, setBal] = useState([]);
  const [open, setOpen] = useState(false);
  const load = () => { api.get("/leaves").then(({ data }) => setRows(data.map((r) => ({ ...r, kind: "leave" })))); api.get("/leaves/balance").then(({ data }) => setBal(data)); };
  useEffect(() => { load(); }, []);
  const decide = async (r, status) => { try { await api.patch(`/leaves/${r.id}`, { status }); toast.success(`Leave ${status.toLowerCase()}`); load(); } catch (e) { err(e); } };
  const columns = [
    ...(isMgr ? [{ key: "user_name", label: "Employee", fixed: true, render: (r) => <span className="font-semibold">{r.user_name}</span> }] : []),
    { key: "type", label: "Type" },
    { key: "from_date", label: "From", render: (r) => fmtDate(r.from_date) },
    { key: "to_date", label: "To", render: (r) => fmtDate(r.to_date) },
    { key: "days", label: "Days", render: (r) => <span className="font-semibold">{r.days}{r.half_day ? " (half)" : ""}</span> },
    { key: "reason", label: "Reason", render: (r) => <span className="text-xs">{r.reason}</span> },
    { key: "status", label: "Status", render: (r) => <StatusBadge value={r.status} /> },
    { key: "lop_days", label: "LOP", hidden: true, render: (r) => r.lop_days || "—" },
    { key: "created_at", label: "Applied On", hidden: true, render: (r) => <span className="text-xs">{fmtDateTime(r.created_at)}</span> },
    { key: "act", label: "", fixed: true, render: (r) => <DecideButtons r={r} onDecide={decide} isMgr={isMgr} me={me} isAdmin={isAdmin} /> },
  ];
  return (
    <>
      <div className="flex flex-wrap items-stretch gap-3 mb-4" data-testid="leave-balance">
        {bal.filter((b) => b.quota).map((b) => (
          <div key={b.type} className="bg-white border border-slate-200 rounded-xl px-4 py-3 min-w-[150px]"><div className="text-[11px] uppercase tracking-wider text-slate-500">{b.type}</div><div className="font-heading text-2xl font-extrabold text-[#312E81]">{b.balance}<span className="text-sm text-slate-400 font-medium"> / {b.quota}</span></div><div className="text-[11px] text-slate-400">{b.used} used{b.pending ? ` · ${b.pending} pending` : ""} this year</div></div>
        ))}
        <Button onClick={() => setOpen(true)} className="ml-auto self-center bg-[#4F46E5] hover:bg-[#4338CA]" data-testid="leave-apply-btn"><CalendarPlus className="w-4 h-4 mr-1" /> Apply Leave</Button>
      </div>
      <DataTable columns={columns} rows={rows} loading={rows === null} testid="leaves-table" rowTestid="leave-row" columnsKey="leaves" empty="No leave requests" />
      <LeaveDialog open={open} onOpenChange={setOpen} onSaved={load} types={bal} />
    </>
  );
}

function Regularisation({ isMgr, me, isAdmin }) {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({});
  const load = () => api.get("/attendance/regularizations").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);
  const decide = async (r, status) => { try { await api.patch(`/attendance/regularizations/${r.id}`, { status }); toast.success(`Request ${status.toLowerCase()}`); load(); } catch (e) { err(e); } };
  const submit = async () => { try { await api.post("/attendance/regularizations", f); toast.success("Regularisation request submitted"); setOpen(false); load(); } catch (e) { err(e); } };
  const columns = [
    ...(isMgr ? [{ key: "user_name", label: "Employee", fixed: true, render: (r) => <span className="font-semibold">{r.user_name}</span> }] : []),
    { key: "date", label: "Date", render: (r) => fmtDate(r.date) },
    { key: "in_time", label: "In" },
    { key: "out_time", label: "Out" },
    { key: "reason", label: "Reason", render: (r) => <span className="text-xs">{r.reason}</span> },
    { key: "status", label: "Status", render: (r) => <StatusBadge value={r.status} /> },
    { key: "act", label: "", fixed: true, render: (r) => <DecideButtons r={r} onDecide={decide} isMgr={isMgr} me={me} isAdmin={isAdmin} /> },
  ];
  return (
    <>
      <div className="flex justify-between items-center mb-3">
        <p className="text-sm text-slate-500">Forgot to clock in or out? Request a correction — it updates the attendance log once approved.</p>
        <Button onClick={() => { setF({ date: today(), in_time: "09:30", out_time: "18:30", reason: "" }); setOpen(true); }} className="bg-[#4F46E5] hover:bg-[#4338CA]" data-testid="reg-apply-btn"><Clock className="w-4 h-4 mr-1" /> Request Regularisation</Button>
      </div>
      <DataTable columns={columns} rows={rows} loading={rows === null} testid="reg-table" rowTestid="reg-row" columnsKey="regularisation" empty="No regularisation requests" />
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white"><DialogHeader><DialogTitle>Attendance Regularisation</DialogTitle></DialogHeader>
          <div className="grid grid-cols-3 gap-3">
            <div><Label>Date</Label><Input type="date" max={today()} value={f.date || ""} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="reg-date" /></div>
            <div><Label>In time</Label><Input type="time" value={f.in_time || ""} onChange={(e) => setF({ ...f, in_time: e.target.value })} data-testid="reg-in" /></div>
            <div><Label>Out time</Label><Input type="time" value={f.out_time || ""} onChange={(e) => setF({ ...f, out_time: e.target.value })} data-testid="reg-out" /></div>
            <div className="col-span-3"><Label>Reason</Label><Textarea value={f.reason || ""} onChange={(e) => setF({ ...f, reason: e.target.value })} data-testid="reg-reason" /></div>
          </div>
          <DialogFooter><Button onClick={submit} className="bg-[#4F46E5] hover:bg-[#4338CA]" data-testid="reg-submit">Submit</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function Attendance() {
  const { user } = useAuth();
  const isMgr = MGR.includes(user.role);
  const open = ADMIN.includes(user.role);
  return (
    <div>
      <PageHeader title="Attendance" subtitle={isMgr ? "Clock-in log, leave approvals and regularisation for the whole team" : "Your attendance, leaves and regularisation requests"} testid="attendance-header" />
      <Tabs defaultValue="log">
        <TabsList className="mb-4">
          <TabsTrigger value="log" data-testid="att-tab-log">Attendance Log</TabsTrigger>
          <TabsTrigger value="leaves" data-testid="att-tab-leaves">Leave Management</TabsTrigger>
          <TabsTrigger value="reg" data-testid="att-tab-reg">Regularisation</TabsTrigger>
          {open && <TabsTrigger value="types" data-testid="att-tab-types">Leave Types</TabsTrigger>}
        </TabsList>
        <TabsContent value="log"><AttendanceLog isMgr={isMgr || user.role === "accounts"} /></TabsContent>
        <TabsContent value="leaves"><Leaves isMgr={isMgr} me={user.id} isAdmin={open} /></TabsContent>
        <TabsContent value="reg"><Regularisation isMgr={isMgr} me={user.id} isAdmin={open} /></TabsContent>
        {open && <TabsContent value="types"><LeaveTypes /></TabsContent>}
      </Tabs>
    </div>
  );
}
